import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Save, Users, ShieldCheck } from "lucide-react";
import { HelpHint } from "@/components/HelpHint";
import { toast } from "sonner";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import UsersPage from "@/pages/Users";
import BankHolidaysPage from "@/pages/BankHolidays";
import AuditTrailPage from "@/pages/AuditTrail";
import PermissionsCard from "@/pages/settings/PermissionsCard";
import PageContentCard from "@/pages/settings/PageContentCard";
import LeaveYearsCard from "@/pages/settings/LeaveYearsCard";
import RewardSettingsCard from "@/pages/settings/RewardSettingsCard";
import DocumentTypesCard from "@/pages/settings/DocumentTypesCard";
import ReviewSubjectsCard from "@/pages/settings/ReviewSubjectsCard";
import EmployerNISettingsCard from "@/pages/settings/EmployerNISettingsCard";
import HelpArticlesCard from "@/pages/settings/HelpArticlesCard";
import BenefitCatalogueManager from "@/pages/settings/BenefitCatalogueCard";
import HomeContentCard from "@/pages/settings/HomeContentCard";
import TemplatesManager from "@/pages/settings/TemplatesManager";
import EmailTemplatesManager from "@/pages/settings/EmailTemplatesManager";
import LearningCatalogueCard from "@/pages/settings/LearningCatalogueCard";

export default function Settings() {
  const { org, loadOrg } = useAuth();
  const [orgForm, setOrgForm] = useState({ org_name: "", address: "", default_region: "england-and-wales", logo_url: "", max_concurrent_annual: 0 });

  useEffect(() => { if (org) setOrgForm({ org_name: org.org_name || "", address: org.address || "", default_region: org.default_region || "england-and-wales", logo_url: org.logo_url || "", max_concurrent_annual: org.max_concurrent_annual || 0 }); }, [org]);

  const saveOrg = async () => {
    try { await api.put("/org", { ...orgForm, max_concurrent_annual: Number(orgForm.max_concurrent_annual) || 0 }); toast.success("Organisation saved"); loadOrg(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clearDemo = async () => {
    try {
      const { data } = await api.post("/admin/clear-demo-data");
      toast.success(`${data.message} (${data.total} records removed)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Settings" subtitle="Company-wide rules controlling calculation and approval." />
      <Tabs defaultValue="years" className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        <TabsList className="flex lg:flex-col h-auto lg:w-full bg-white border border-slate-200 rounded-xl p-2 gap-1 lg:sticky lg:top-32">
          <TabsTrigger value="years" data-testid="tab-years" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Holiday years</TabsTrigger>
          <TabsTrigger value="org" data-testid="tab-org" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Organisation</TabsTrigger>
          <TabsTrigger value="leave-types" data-testid="tab-leave-types" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Leave types</TabsTrigger>
          <TabsTrigger value="reward" data-testid="tab-reward-settings" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Reward &amp; benefits</TabsTrigger>
          <TabsTrigger value="employer-ni" data-testid="tab-employer-ni" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Employer NI</TabsTrigger>
          <TabsTrigger value="benefit-catalogue" data-testid="tab-benefit-catalogue" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Benefit catalogue</TabsTrigger>
          <TabsTrigger value="document-types" data-testid="tab-document-types" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Document types</TabsTrigger>
          <TabsTrigger value="review-subjects" data-testid="tab-review-subjects" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Review subjects</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Homepage content</TabsTrigger>
          <TabsTrigger value="learning" data-testid="tab-learning" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Learning catalogue</TabsTrigger>
          <TabsTrigger value="help" data-testid="tab-help" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Help articles</TabsTrigger>
          <TabsTrigger value="permissions" data-testid="tab-permissions" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Roles &amp; permissions</TabsTrigger>
          <TabsTrigger value="page-content" data-testid="tab-page-content" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Page content</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Templates</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Recruitment emails</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Users</TabsTrigger>
          <TabsTrigger value="bank-holidays" data-testid="tab-bank-holidays" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Bank holidays</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" className="w-full lg:justify-start data-[state=active]:bg-[#EFF8FF] data-[state=active]:text-[#2EA3FD] data-[state=active]:shadow-none">Audit trail</TabsTrigger>
        </TabsList>
        <div className="lg:col-span-3 space-y-4">

        <TabsContent value="years" className="space-y-4">
          <LeaveYearsCard />
        </TabsContent>

        <TabsContent value="permissions" className="space-y-4">
          <PermissionsCard />
        </TabsContent>

        <TabsContent value="page-content" className="space-y-4">
          <PageContentCard />
        </TabsContent>

        <TabsContent value="org">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 max-w-2xl">
            <div><Label className="text-xs text-slate-500">Organisation name</Label><Input value={orgForm.org_name} onChange={(e) => setOrgForm({ ...orgForm, org_name: e.target.value })} data-testid="org-name-input" /></div>
            <div><Label className="text-xs text-slate-500">Address</Label><Input value={orgForm.address} onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })} /></div>
            <div>
              <Label className="text-xs text-slate-500">Logo</Label>
              <div className="flex items-center gap-3 mt-1">
                {orgForm.logo_url ? <img src={orgForm.logo_url} alt="logo" className="h-10 max-w-[160px] object-contain bg-slate-50 border border-slate-200 rounded px-2" data-testid="org-logo-preview" /> : <span className="text-xs text-slate-400">No logo set</span>}
                <Input type="file" accept="image/*" className="text-xs max-w-xs" data-testid="org-logo-file"
                  onChange={async (ev) => {
                    const file = ev.target.files?.[0]; if (!file) return;
                    if (file.size > 1500000) { toast.error("Logo too large — use an image under 1.5MB"); return; }
                    const fd = new FormData(); fd.append("file", file);
                    try { const { data } = await api.post("/org/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setOrgForm((s) => ({ ...s, logo_url: data.logo_url })); toast.success("Logo uploaded — click Save organisation to apply"); }
                    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                  }} />
                {orgForm.logo_url && <Button type="button" variant="ghost" size="sm" className="text-rose-600 h-8" onClick={() => setOrgForm({ ...orgForm, logo_url: "" })} data-testid="org-logo-remove">Remove</Button>}
              </div>
              <p className="text-xs text-slate-400 mt-1">Upload a PNG/JPG/SVG, or paste an image URL below. Remember to Save.</p>
            </div>
            <div><Label className="text-xs text-slate-500">Logo URL (optional)</Label><Input value={orgForm.logo_url?.startsWith("data:") ? "" : orgForm.logo_url} placeholder={orgForm.logo_url?.startsWith("data:") ? "Uploaded image in use" : ""} onChange={(e) => setOrgForm({ ...orgForm, logo_url: e.target.value })} data-testid="org-logo-url-input" /></div>
            <div>
              <Label className="text-xs text-slate-500">Default region</Label>
              <Select value={orgForm.default_region} onValueChange={(v) => setOrgForm({ ...orgForm, default_region: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="england-and-wales">England & Wales</SelectItem><SelectItem value="scotland">Scotland</SelectItem><SelectItem value="northern-ireland">Northern Ireland</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1">Max staff on annual leave at once (0 = no limit) <HelpHint text="When this many people already have overlapping annual leave, employees see a warning (they can still book) and approvers are alerted. Set 0 to switch the check off." testid="org-maxannual-hint" /></Label>
              <Input type="number" min="0" value={orgForm.max_concurrent_annual} onChange={(e) => setOrgForm({ ...orgForm, max_concurrent_annual: e.target.value })} data-testid="org-max-annual-input" />
              <p className="text-xs text-slate-400 mt-1">When this many staff already have annual leave overlapping a requested period, employees see a warning (they can still submit) and approvers are alerted.</p>
            </div>
            <Button onClick={saveOrg} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="org-save-btn">Save organisation</Button>
          </div>

          <div className="bg-white border border-rose-200 rounded-xl p-6 shadow-sm max-w-2xl mt-6" data-testid="danger-zone">
            <h3 className="font-semibold text-rose-700 flex items-center gap-2"><Trash2 size={18} />Danger zone — clear demo data</h3>
            <p className="text-sm text-slate-600 mt-1 mb-4">Permanently removes all sample employees, working patterns, allowances, adjustments, holiday requests, notifications and the audit log, and stops the demo data from re-seeding. Your admin login, organisation settings, holiday-year settings and bank holidays are kept. Use this before going live. This cannot be undone.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="clear-demo-btn"><Trash2 size={15} className="mr-2" />Clear demo data</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all demo data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes every sample employee and all their holiday records, and prevents the demo data from re-appearing. Your admin account and settings are preserved. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="clear-demo-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearDemo} className="bg-rose-600 hover:bg-rose-700" data-testid="clear-demo-confirm">Yes, clear demo data</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TabsContent>

        <TabsContent value="leave-types">
          <LeaveTypesManager />
        </TabsContent>
        <TabsContent value="reward">
          <RewardSettingsCard />
        </TabsContent>
        <TabsContent value="employer-ni">
          <EmployerNISettingsCard />
        </TabsContent>
        <TabsContent value="benefit-catalogue">
          <BenefitCatalogueManager />
        </TabsContent>
        <TabsContent value="document-types">
          <DocumentTypesCard />
        </TabsContent>
        <TabsContent value="review-subjects">
          <ReviewSubjectsCard />
        </TabsContent>
        <TabsContent value="content">
          <HomeContentCard />
        </TabsContent>
        <TabsContent value="learning">
          <LearningCatalogueCard />
        </TabsContent>
        <TabsContent value="help">
          <HelpArticlesCard />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesManager />
        </TabsContent>
        <TabsContent value="emails">
          <EmailTemplatesManager />
        </TabsContent>
        <TabsContent value="users">
          <UsersPage />
        </TabsContent>
        <TabsContent value="bank-holidays">
          <BankHolidaysPage />
        </TabsContent>
        <TabsContent value="audit">
          <AuditTrailPage />
        </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function LeaveTypesManager() {
  const { types, reload } = useLeaveTypes();
  const [f, setF] = useState({ name: "", color: "#2EA3FD", deducts: false });
  const add = async () => {
    if (!f.name) { toast.error("Enter a name"); return; }
    try { await api.post("/leave-types", f); toast.success("Leave type added"); setF({ name: "", color: "#2EA3FD", deducts: false }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async (t, changes) => {
    try { await api.put(`/leave-types/${t.id}`, { name: t.name, color: t.color, deducts: t.deducts, active: t.active, ...changes }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (t) => {
    try { await api.delete(`/leave-types/${t.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Types marked <strong>Deducts balance</strong> reduce holiday entitlement (e.g. annual leave). Non-deducting types (birthday, Christmas closure, study leave) can still be booked and appear on the calendar without affecting the balance.</p>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100" data-testid="leave-types-list">
        {types.map((t) => (
          <div key={t.id} className="flex items-center gap-3 p-3 flex-wrap" data-testid={`leave-type-row-${t.key}`}>
            <span className="h-5 w-5 rounded-full border" style={{ backgroundColor: t.color }} />
            <span className="font-medium text-[#0B1A2F] flex-1 min-w-[10rem]">{t.name} {t.system && <span className="text-[10px] text-slate-400">built-in</span>}</span>
            <label className="flex items-center gap-2 text-xs text-slate-600">Deducts balance<Switch checked={t.deducts} onCheckedChange={(v) => save(t, { deducts: v })} data-testid={`lt-deducts-${t.key}`} /></label>
            <label className="flex items-center gap-2 text-xs text-slate-600">Active<Switch checked={t.active} onCheckedChange={(v) => save(t, { active: v })} data-testid={`lt-active-${t.key}`} /></label>
            {!t.system && <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(t)} data-testid={`lt-delete-${t.key}`}><Trash2 size={14} /></Button>}
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-[#0B1A2F] mb-3">Add a leave type</h4>
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label className="text-xs text-slate-500 mb-1 block">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Compassionate leave" data-testid="lt-name-input" className="w-56" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Colour</Label><Input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="w-16 h-10 p-1" data-testid="lt-color-input" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-600 mb-2">Deducts balance<Switch checked={f.deducts} onCheckedChange={(v) => setF({ ...f, deducts: v })} data-testid="lt-new-deducts" /></label>
          <Button onClick={add} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="lt-add-btn"><Plus size={15} className="mr-1" />Add</Button>
        </div>
      </div>
    </div>
  );
}


